<via-help-resources-layout role="main">
    <h1 class="e-page-title">Methodology</h1>
    <p>Learn more about how we collect, match and organise data in Via</p>

    <h2 class="e-content-heading">Data science combined with years of industry expertise</h2>
    <p>
        Via is built using a combination of our longstanding research expertise and web data
        extraction technology. We extract data from websites for:
    </p>
    <ul>
        <li>Products (unique SKUs)</li>
        <li>Product Attributes</li>
        <li>Product Price</li>
        <li>Product Promotions</li>
        <li>Product Photos</li>
    </ul>
    <p>
        This is done by extracting text (product descriptions, prices and other details), images
        (product photos), and product taxonomy (how every product is categorised/classified in each
        respective website).
    </p>
    <p>
        Using advanced Data Science and Artificial Intelligence (AI) techniques, all this extracted
        product/SKU-specific data is then automatically mapped to fit into Euromonitor's established
        category taxonomy. This taxonomy has been built up over many years and allows us to place
        our web extracted data into an organised framework with input from our industry and country
        analysts to ensure the data is well organised and cross-comparable across countries, product
        categories, suppliers, brands, SKUs and retailers.
    </p>
    <p>
        We use Natural Language Processing (NLP) to teach the AI behind Via how to process and
        analyse large amounts of textual data. This in turn helps us to understand and correctly
        classify and match every SKU we capture from every e-commerce website in our scope. This
        also provides us with information on the product category, flavour/fragrance, ingredients,
        packaging, nutritional information and product claims.
    </p>

    <h2 class="e-content-heading">Overview of web data extraction and data science workflow</h2>
    <img src="/assets/images/help-resources/data-workflow-overview.png" alt="Data flow diagram">

    <h2 class="e-content-heading">Image recognition</h2>
    <p>
        Image recognition techniques help Via accurately track the exact same product across
        multiple different stores.
    </p>
    <p>
        We use neural networks to help ensure that the exact same SKUs are being matched correctly
        across every website they are found in a country, and then correctly categorised into our
        standardised taxonomy.
    </p>
    <p>
        Ultimately this automatic mapping of SKUs to Euromonitor's taxonomy ensures that everything
        in Via is accurately organised and cross-comparable, so we can distil meaningful insight
        from our web extracted data. This process is fully automated. Euromonitor's Data Science
        pipeline processes terabytes of data over a 24-hour cycle to ensure clients have the most
        up-to-date information possible, including daily updates on: product prices and price
        changes, product features, new product launches or delisted/discontinued products.
    </p>
    <img src="/assets/images/help-resources/image-recognition.jpeg" alt="Image recognition technique illustration">

    <h2 class="e-content-heading">Maintaining and improving accuracy</h2>
    <p>
        Our research team also help us ensure our web extracted data is accurately organised and
        matched against Euromonitor's category taxonomy, as well as our company and brand name
        database. Our entire network of global research analysts, managers, and directors manually
        reviews and labels tens of the thousands of SKUs we have extracted from retailer websites
        around the world against our own taxonomy. This manual work ensures that the Artificial
        Intelligence (AI) behind Via is matching web extracted SKUs to the correct industries and
        categories. This process of manually teaching the machine to correctly classify SKUs to a
        category taxonomy is known as "Supervised Training."
    </p>
    <p>
        By combining data science with the expertise of our research team, instead of just relying
        on algorithms, helps us to check and then 'train' our AI to ensure continued - if not
        improved - accuracy.
    </p>
</via-help-resources-layout>
